import {
  getMultiFactorResolver,
  type MultiFactorError,
  type MultiFactorResolver,
  signInWithEmailAndPassword,
  TotpMultiFactorGenerator,
} from 'firebase/auth';
import { initFirebase } from '../firebase.js';

export class AnmeldeFehler extends Error {
  constructor(readonly text: string) {
    super(text);
    this.name = 'AnmeldeFehler';
  }
}

export type AnmeldeErgebnis = { art: 'fertig' } | { art: 'zweiter-faktor'; faktorUid: string };

export interface AnmeldeDienst {
  melde(email: string, passwort: string): Promise<AnmeldeErgebnis>;
  loeseZweitenFaktor(faktorUid: string, code: string): Promise<void>;
}

const FALSCHE_ZUGANGSDATEN = 'E-Mail oder Passwort ist falsch.';

function codeVon(fehler: unknown): string {
  return typeof (fehler as { code?: unknown }).code === 'string'
    ? (fehler as { code: string }).code
    : '';
}

/**
 * Bewusst unspezifisch bei allem, was mit den Zugangsdaten zu tun hat: Eine Meldung, die
 * "Konto existiert nicht" von "Passwort falsch" unterscheidet, verraet, welche Adressen
 * Konten haben.
 */
function anmeldeMeldung(code: string): string {
  switch (code) {
    case 'auth/too-many-requests':
      return 'Zu viele Versuche. Bitte warten Sie einige Minuten.';
    case 'auth/network-request-failed':
      return 'Keine Verbindung. Bitte prüfen Sie Ihre Internetverbindung.';
    default:
      return FALSCHE_ZUGANGSDATEN;
  }
}

function codeMeldung(code: string): string {
  switch (code) {
    case 'auth/invalid-verification-code':
    case 'auth/invalid-payload':
      return 'Der Code ist nicht richtig. Bitte prüfen Sie die Zeit auf Ihrem Gerät.';
    case 'auth/too-many-requests':
      return 'Zu viele Versuche. Bitte warten Sie einige Minuten.';
    default:
      return 'Der Code konnte nicht bestätigt werden. Bitte melden Sie sich erneut an.';
  }
}

export function firebaseAnmeldeDienst(): AnmeldeDienst {
  // Der Resolver traegt die halbfertige Anmeldung zwischen den beiden Schritten. Er lebt
  // ausschliesslich hier im Speicher -- nichts davon gehoert in Storage oder in die URL.
  let resolver: MultiFactorResolver | null = null;

  return {
    async melde(email, passwort) {
      const { auth } = await initFirebase();
      try {
        await signInWithEmailAndPassword(auth, email, passwort);
        resolver = null;
        return { art: 'fertig' };
      } catch (fehler) {
        const code = codeVon(fehler);
        if (code !== 'auth/multi-factor-auth-required') {
          throw new AnmeldeFehler(anmeldeMeldung(code));
        }
        resolver = getMultiFactorResolver(auth, fehler as MultiFactorError);
        const totp = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
        if (!totp) {
          throw new AnmeldeFehler(
            'Für dieses Konto ist kein Authenticator eingerichtet. Bitte wenden Sie sich an die Administration.',
          );
        }
        return { art: 'zweiter-faktor', faktorUid: totp.uid };
      }
    },

    async loeseZweitenFaktor(faktorUid, code) {
      if (!resolver) {
        throw new AnmeldeFehler('Die Anmeldung ist abgelaufen. Bitte melden Sie sich erneut an.');
      }
      try {
        await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(faktorUid, code));
        resolver = null;
      } catch (fehler) {
        throw new AnmeldeFehler(codeMeldung(codeVon(fehler)));
      }
    },
  };
}
